import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Block,
  Row,
  Col,
  PeriodFilter,
  Button,
} from "../../../components/Component";
import TeleconsultationPageShell from "../../../components/admin/TeleconsultationPageShell";
import { Card } from "reactstrap";
import { Doughnut } from "react-chartjs-2";
import http from "../../../utils/http-common";
import { getPublicUrl } from "../../../utils/Utils";
import { useHistory } from "react-router-dom";
import moment from "moment";
import AdminSideDrawer from "../../../components/admin/AdminSideDrawer";

const STATUS_KEYS = [
  "scheduled",
  "paid",
  "completed",
  "cancelled",
  "no_show",
  "no_show_patient",
  "no_show_provider",
  "technical_issue",
];

const emptyStatusCounts = () =>
  STATUS_KEYS.reduce((acc, k) => {
    acc[k] = 0;
    return acc;
  }, {});

const normalizeStatus = (s) => {
  let x = String(s || "").toLowerCase().replace(/-/g, "_");
  if (x === "canceled") x = "cancelled";
  return x;
};

const TeleconsultationStats = () => {
  const history = useHistory();
  const [period, setPeriod] = useState("30d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [clinicDrawerRow, setClinicDrawerRow] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const days = period === "7d" ? 7 : period === "30d" ? 30 : period === "90d" ? 90 : null;
    const listParams =
      days == null
        ? { limit: 500 }
        : {
            dateFrom: moment().subtract(days, "days").startOf("day").toISOString(),
            dateTo: moment().endOf("day").toISOString(),
            limit: 500,
          };

    try {
      const [listRes, statsRes] = await Promise.all([
        http.get("/api/v1/consultations", {
          params: listParams,
        }),
        http.get("/api/v1/consultations/admin/stats"),
      ]);
      const list = listRes.data?.data || listRes.data?.consultations || listRes.data || [];
      setConsultations(Array.isArray(list) ? list : []);
      setAdminStats(statsRes.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Failed to load statistics");
      setConsultations([]);
      setAdminStats(null);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const { totalInPeriod, statusCounts, clinicRows } = useMemo(() => {
    const counts = emptyStatusCounts();
    const byClinic = new Map();

    consultations.forEach((c) => {
      const st = normalizeStatus(c.status);
      if (counts.hasOwnProperty(st)) counts[st] += 1;

      const cid = c.clinic?._id || c.clinic?.id || c.clinic;
      const cname =
        typeof c.clinic === "object" && c.clinic
          ? c.clinic.name || "Clinic"
          : cid
          ? String(cid).slice(-8)
          : "—";
      const key = cid ? String(cid) : cname;
      if (!byClinic.has(key)) {
        byClinic.set(key, {
          clinicId: cid,
          clinicName: cname,
          total: 0,
          ...emptyStatusCounts(),
        });
      }
      const row = byClinic.get(key);
      row.total += 1;
      if (counts.hasOwnProperty(st)) row[st] += 1;
    });

    const rows = Array.from(byClinic.values()).sort((a, b) => b.total - a.total);
    return {
      totalInPeriod: consultations.length,
      statusCounts: counts,
      clinicRows: rows,
    };
  }, [consultations]);

  const doughnutData = useMemo(
    () => ({
      labels: STATUS_KEYS.map((k) => k.replace(/_/g, " ")),
      datasets: [
        {
          data: STATUS_KEYS.map((k) => statusCounts[k] || 0),
          backgroundColor: [
            "#798bff",
            "#1ee0ac",
            "#54d3c2",
            "#e85347",
            "#8094ae",
            "#f4bd0e",
            "#ff6b6b",
            "#a29bfe",
          ],
          borderWidth: 0,
        },
      ],
    }),
    [statusCounts]
  );

  const partnersByTcRows = adminStats?.partnersByTc || [];
  const totalAllTime = adminStats?.totalConsultations;

  return (
    <>
      <AdminSideDrawer
        open={!!clinicDrawerRow}
        onClose={() => setClinicDrawerRow(null)}
        title={clinicDrawerRow ? clinicDrawerRow.clinicName || "Clinic" : ""}
        subtitle={clinicDrawerRow ? `Total in period: ${clinicDrawerRow.total}` : ""}
        footer={
          clinicDrawerRow?.clinicId ? (
            <>
              <Button
                size="sm"
                color="primary"
                type="button"
                onClick={() => history.push(getPublicUrl(`clinics/${clinicDrawerRow.clinicId}/prices`))}
              >
                Prices
              </Button>
              <Button
                size="sm"
                color="light"
                type="button"
                onClick={() => history.push(getPublicUrl(`clinics/${clinicDrawerRow.clinicId}/calendar`))}
              >
                Calendar
              </Button>
              <Button
                size="sm"
                color="light"
                type="button"
                onClick={() => history.push(getPublicUrl(`clinics/${clinicDrawerRow.clinicId}/practitioners`))}
              >
                Team
              </Button>
              <Button size="sm" color="light" type="button" onClick={() => setClinicDrawerRow(null)}>
                Close
              </Button>
            </>
          ) : (
            <Button size="sm" color="light" type="button" onClick={() => setClinicDrawerRow(null)}>
              Close
            </Button>
          )
        }
      >
        {clinicDrawerRow ? (
          <dl className="row small mb-0">
            {STATUS_KEYS.map((k) => (
              <React.Fragment key={k}>
                <dt className="col-6 text-soft">{k.replace(/_/g, " ")}</dt>
                <dd className="col-6 mb-2">{clinicDrawerRow[k] ?? 0}</dd>
              </React.Fragment>
            ))}
          </dl>
        ) : null}
      </AdminSideDrawer>
      <TeleconsultationPageShell
        headTitle="TC statistics"
        title="Teleconsultation statistics"
        description="Consultation counts for the selected period; partner approval counts are all-time (admin)."
        aside={<PeriodFilter period={period} onPeriodChange={setPeriod} />}
      >
        <Block>
          {loading ? (
            <p className="text-soft">Loading…</p>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : (
            <>
              <Row className="g-gs mb-3">
                <Col md="6" lg="4" xl={12 / 5}>
                  <Card className="card-bordered h-100">
                    <div className="card-inner">
                      <h6 className="overline-title text-soft">Total (period)</h6>
                      <p className="fs-2 mb-0">{totalInPeriod}</p>
                      <p className="small text-soft mb-0">
                        {period === "7d"
                          ? "Last 7 days"
                          : period === "30d"
                          ? "Last 30 days"
                          : period === "90d"
                          ? "Last 90 days"
                          : "All (up to 500 loaded)"}
                      </p>
                    </div>
                  </Card>
                </Col>
                {STATUS_KEYS.map((key) => (
                  <Col md="6" lg="4" xl={12 / 5} key={key}>
                    <Card className="card-bordered h-100">
                      <div className="card-inner">
                        <h6 className="overline-title text-soft">{key.replace(/_/g, " ")}</h6>
                        <p className="fs-3 mb-0">{statusCounts[key] ?? 0}</p>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>

              <Row className="g-gs mb-3">
                <Col lg="5">
                  <Card className="card-bordered h-100">
                    <div className="card-inner">
                      <h6 className="overline-title mb-3">Status distribution (period)</h6>
                      <div style={{ maxHeight: 280, position: "relative" }}>
                        <Doughnut
                          data={doughnutData}
                          options={{
                            legend: { position: "bottom", labels: { boxWidth: 12 } },
                            maintainAspectRatio: false,
                            cutoutPercentage: 55,
                          }}
                        />
                      </div>
                    </div>
                  </Card>
                </Col>
                <Col lg="7">
                  <Card className="card-bordered h-100">
                    <div className="card-inner">
                      <h6 className="overline-title mb-2">Partners — TC approval (all-time)</h6>
                      <p className="small text-soft mb-3">
                        Total consultations (all-time, system): <strong>{totalAllTime ?? "—"}</strong>
                      </p>
                      {partnersByTcRows.length === 0 ? (
                        <p className="text-soft small mb-0">No partner data.</p>
                      ) : (
                        <ul className="list-unstyled small mb-0">
                          {partnersByTcRows.map((row) => (
                            <li key={String(row._id ?? "unknown")} className="d-flex justify-content-between py-1 border-bottom">
                              <span className="text-soft">Approval: {row._id || "—"}</span>
                              <span>{row.n}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </Card>
                </Col>
              </Row>

              <Card className="card-bordered">
                <div className="card-inner">
                  <h6 className="overline-title mb-3">Clinics — consultations in period</h6>
                  {clinicRows.length === 0 ? (
                    <p className="text-soft small mb-0">No consultations in this period.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-striped mb-0">
                        <thead>
                          <tr>
                            <th>Clinic</th>
                            <th className="text-end">Total</th>
                            <th className="text-end">Scheduled</th>
                            <th className="text-end">Paid</th>
                            <th className="text-end">Completed</th>
                            <th className="text-end">Cancelled</th>
                            <th className="text-end">No-show</th>
                            <th className="text-end">No-show patient</th>
                            <th className="text-end">No-show provider</th>
                            <th className="text-end">Technical</th>
                          </tr>
                        </thead>
                        <tbody>
                          {clinicRows.map((row, idx) => (
                            <tr
                              key={`${String(row.clinicId || row.clinicName)}-${idx}`}
                              style={{ cursor: "pointer" }}
                              onClick={() => setClinicDrawerRow(row)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setClinicDrawerRow(row);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              <td className="small">{row.clinicName}</td>
                              <td className="text-end small">{row.total}</td>
                              <td className="text-end small">{row.scheduled}</td>
                              <td className="text-end small">{row.paid}</td>
                              <td className="text-end small">{row.completed}</td>
                              <td className="text-end small">{row.cancelled}</td>
                              <td className="text-end small">{row.no_show}</td>
                              <td className="text-end small">{row.no_show_patient}</td>
                              <td className="text-end small">{row.no_show_provider}</td>
                              <td className="text-end small">{row.technical_issue}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </Card>
            </>
          )}
        </Block>
      </TeleconsultationPageShell>
    </>
  );
};

export default TeleconsultationStats;
